import * as dom from "./dom.js";
import { initIcons } from "../icons";

type ImageVariant = {
    label: string;
    src: string;
    fileName: string;
};

export class ImageLightbox {
    private images: HTMLImageElement[];
    private index = 0;
    private variants: ImageVariant[] = [];

    private overlay!: HTMLElement;
    private img!: HTMLImageElement;
    private loader!: HTMLElement;
    private caption!: HTMLElement;
    private captionTitle!: HTMLElement;
    private formatSelect!: HTMLSelectElement;
    private download!: HTMLAnchorElement;

    constructor(selector: string) {
        this.images = [...document.querySelectorAll<HTMLImageElement>(selector)];
        if (!this.images.length) return;

        this.overlay = document.createElement("div");
        this.overlay.className =
            "fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center hidden";

        this.img = document.createElement("img");
        this.img.className =
            "max-w-[95vw] max-h-[90vh] object-contain transition-opacity duration-300 opacity-0";

        this.loader = document.createElement("div");
        this.loader.className =
            "absolute flex items-center justify-center hidden";

        this.loader.innerHTML = `
            <div class="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
        `;

        this.caption = document.createElement("div");
        this.caption.className =
            "absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 px-3 py-1 rounded flex items-center gap-2 max-w-[90vw]";

        this.captionTitle = document.createElement("span");
        this.captionTitle.className = "max-w-[52vw] truncate";

        this.formatSelect = document.createElement("select");
        this.formatSelect.className =
            "bg-black/70 text-white border border-white/30 rounded px-2 py-1 text-xs";
        this.formatSelect.setAttribute("aria-label", "Image type");
        this.formatSelect.onchange = () => {
            this.loadVariant(Number(this.formatSelect.value), false);
        };

        const prev = document.createElement("button");
        prev.className =
            "absolute left-4 text-white text-3xl hover:opacity-70";
        prev.innerHTML = "❮";
        prev.onclick = () => this.show(this.index - 1);

        const next = document.createElement("button");
        next.className =
            "absolute right-4 text-white text-3xl hover:opacity-70";
        next.innerHTML = "❯";
        next.onclick = () => this.show(this.index + 1);

        const close = document.createElement("button");
        close.className =
            "absolute top-4 right-4 text-white text-3xl hover:opacity-70";
        close.innerHTML = "×";
        close.onclick = () => this.close();

        this.download = document.createElement("a");
        this.download.className =
            "hidden inline-flex items-center gap-1 underline text-white";
        this.download.rel = "nofollow noopener noreferrer";
        this.download.setAttribute("download", "");
        this.download.innerHTML = `
            <i data-lucide="download" class="w-4 h-4"></i>
            <span>Download</span>
        `;
        this.download.onclick = e => e.stopPropagation();

        this.caption.append(this.captionTitle, this.formatSelect, this.download);

        this.overlay.append(
            this.img,
            this.loader,
            prev,
            next,
            close,
            this.caption
        );

        document.body.appendChild(this.overlay);

        this.images.forEach((image, i) => {
            image.style.cursor = "zoom-in";
            image.addEventListener("click", () => this.open(i));
        });

        document.addEventListener("keydown", e => {
            if (this.overlay.classList.contains("hidden")) return;

            if (e.key === "Escape") this.close();
            if (e.key === "ArrowRight") this.show(this.index + 1);
            if (e.key === "ArrowLeft") this.show(this.index - 1);
        });

        this.overlay.addEventListener("click", e => {
            if (e.target === this.overlay) this.close();
        });
    }

    open(i: number) {
        this.index = i;
        this.show(i);
        dom.show(this.overlay);
        document.body.classList.add("overflow-hidden");
    }

    close() {
        dom.hide(this.overlay);
        document.body.classList.remove("overflow-hidden");
    }

    show(i: number) {
        if (i < 0) i = 0;
        if (i >= this.images.length) i = this.images.length - 1;
        this.index = i;

        const thumb = this.images[i];
        const picture = thumb.closest("picture");

        this.variants = this.getVariants(thumb, picture);
        this.setFormatOptions();

        const initialVariantIndex = this.variants.length > 0 ? 0 : -1;
        const initialVariant = this.variants[initialVariantIndex];

        this.captionTitle.textContent = this.getImageTitle(
            thumb,
            initialVariant?.src ?? thumb.src
        );

        this.loadVariant(initialVariantIndex, true);

        const prev = this.overlay.querySelector("button:nth-of-type(1)") as HTMLElement;
        const next = this.overlay.querySelector("button:nth-of-type(2)") as HTMLElement;

        dom.toggle_hide(prev, this.index === 0);
        dom.toggle_hide(next, this.index === this.images.length - 1);
    }

    private getVariants(
        thumb: HTMLImageElement,
        picture: Element | null
    ): ImageVariant[] {
        const avif = thumb.dataset.avif
            || this.firstSrcFromSrcSet(picture?.querySelector('source[type="image/avif"]')?.getAttribute("srcset") ?? "");
        const webp = thumb.dataset.webp
            || this.firstSrcFromSrcSet(picture?.querySelector('source[type="image/webp"]')?.getAttribute("srcset") ?? "");
        const jpg = thumb.dataset.jpg || thumb.getAttribute("src") || thumb.src;
        const original = thumb.dataset.original || "";

        return this.uniqueVariants([
            this.toVariant("AVIF", avif),
            this.toVariant("WebP", webp),
            this.toVariant("JPG", jpg),
            this.toVariant("Original", original)
        ]);
    }

    private toVariant(label: string, src: string): ImageVariant | null {
        if (!src) return null;

        return {
            label,
            src,
            fileName: this.fileNameFromUrl(src)
        };
    }

    private uniqueVariants(variants: Array<ImageVariant | null>): ImageVariant[] {
        const seen = new Set<string>();
        const result: ImageVariant[] = [];

        for (const variant of variants) {
            if (!variant || seen.has(variant.src)) continue;

            seen.add(variant.src);
            result.push(variant);
        }

        return result;
    }

    private setFormatOptions() {
        this.formatSelect.replaceChildren(
            ...this.variants.map((variant, index) => {
                const option = document.createElement("option");
                option.value = String(index);
                option.textContent = variant.label;
                return option;
            })
        );

        this.formatSelect.value = "0";
        dom.toggle_hide(this.formatSelect, this.variants.length < 2);
    }

    private loadVariant(index: number, allowFallback: boolean) {
        const variant = this.variants[index];

        if (!variant) {
            this.showFailure();
            return;
        }

        dom.show(this.loader);
        this.img.style.opacity = "0";
        dom.hide(this.download);

        this.img.onload = () => {
            this.img.style.opacity = "1";
            dom.hide(this.loader);

            this.download.href = variant.src;
            this.download.download = variant.fileName;
            dom.show(this.download);

            initIcons();
        };

        this.img.onerror = () => {
            if (allowFallback && index + 1 < this.variants.length) {
                this.formatSelect.value = String(index + 1);
                this.loadVariant(index + 1, true);
                return;
            }

            this.showFailure();
        };

        this.img.src = variant.src;
    }

    private showFailure() {
        dom.hide(this.loader);
        dom.hide(this.download);
        this.img.style.opacity = "0";
        this.captionTitle.textContent = "Failed to load image";
    }

    private getImageTitle(thumb: HTMLImageElement, src: string) {
        const alt = thumb.getAttribute("alt")?.trim();
        if (alt && alt.toLowerCase() !== "image") return alt;

        return this.fileNameFromUrl(src).replace(/\.[^.]+$/, "");
    }

    private fileNameFromUrl(src: string) {
        try {
            const url = new URL(src, window.location.href);
            return decodeURIComponent(url.pathname.split("/").pop() || "image");
        } catch {
            return src.split("/").pop() || "image";
        }
    }

    private firstSrcFromSrcSet(srcset: string) {
        return srcset.split(",")[0]?.trim().split(/\s+/)[0] ?? "";
    }
}
